import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, AlertCircle, Loader2, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '@/i18n/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, resendVerification } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errorMessage) setErrorMessage('');
    if (showResendOption) setShowResendOption(false);
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      toast.error('Entrez votre email');
      return;
    }
    
    setResending(true);
    try {
      await resendVerification(formData.email);
      toast.success('Email de vérification envoyé !');
      setShowResendOption(false);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setShowResendOption(false);

    try {
      console.log('[LOGIN PAGE] Attempting login...');
      await login(formData.email, formData.password);
      console.log('[LOGIN PAGE] Login success!');
      
      toast.success('Connexion réussie !');
      
      // Petit délai pour s'assurer que le state est mis à jour
      setTimeout(() => {
        console.log('[LOGIN PAGE] Navigating to /dashboard');
        navigate('/dashboard', { replace: true });
      }, 100);
      
    } catch (error) {
      console.error('[LOGIN PAGE] Login error:', error);
      
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Erreur de connexion';
      
      setErrorMessage(detail);
      
      // Si email non vérifié (403), proposer de renvoyer l'email
      if (status === 403 && detail.toLowerCase().includes('vérif')) {
        setShowResendOption(true);
      }
      
      toast.error(detail);
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#050505] flex"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url('https://images.unsplash.com/photo-1639323251610-d8218455e596?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md w-full animate-fade-in-up">
          {/* Language Selector */}
          <div className="flex justify-end mb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <Globe className="w-4 h-4 mr-1" />
                  {LANGUAGE_FLAGS[language]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1a1a] border-white/10">
                {['fr', 'en', 'es', 'de', 'pt'].map((lang) => (
                  <DropdownMenuItem 
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    className="text-white hover:bg-white/10 cursor-pointer"
                  >
                    {LANGUAGE_FLAGS[lang]} {LANGUAGE_NAMES[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* TrackVibes Logo */}
          <div className="text-center mb-8">
            <img 
              src="/assets/trackvibes-logo.png" 
              alt="TrackVibes" 
              className="h-20 w-auto mx-auto mb-4"
            />
            <p className="text-gray-400">{t('auth', 'login_subtitle')}</p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p>{errorMessage}</p>
                {showResendOption && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="mt-2 text-neon-cyan hover:underline text-sm font-semibold flex items-center gap-1"
                  >
                    {resending ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> {t('common', 'loading')}</>
                    ) : (
                      `→ ${t('auth', 'verification_sent')}`
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-neon-cyan" />
                {t('common', 'email')}
              </Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth', 'email_placeholder')}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-neon-cyan h-12"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4 text-neon-cyan" />
                {t('common', 'password')}
              </Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-neon-cyan h-12 pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-neon-cyan transition-colors p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-gray-500 hover:text-neon-cyan text-sm"
                >
                  {t('auth', 'forgot_password')}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-6 text-lg btn-neon"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('common', 'loading')}
                </span>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  {t('auth', 'login_button')}
                </>
              )}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              {t('auth', 'no_account')}{' '}
              <Link 
                to="/register" 
                className="text-neon-cyan hover:underline"
                data-testid="register-link"
              >
                {t('common', 'register')}
              </Link>
            </p>
          </div>
          
          {/* Legal Links Footer */}
          <div className="mt-12 pt-6 border-t border-white/10">
            <nav className="flex justify-center gap-4 text-xs text-gray-500">
              <Link to="/privacy" className="hover:text-gray-300">{language === 'fr' ? 'Confidentialité' : 'Privacy'}</Link>
              <span className="text-gray-700">|</span>
              <Link to="/conditions" className="hover:text-gray-300">{language === 'fr' ? 'CGU' : 'Terms'}</Link>
              <span className="text-gray-700">|</span>
              <Link to="/mentions-legales" className="hover:text-gray-300">{language === 'fr' ? 'Mentions légales' : 'Legal'}</Link>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
